import { ErrorMessage, Field } from 'formik';
import PropTypes from 'prop-types';
import { isDesktop, isMobileOnly } from 'react-device-detect';

import useCurrentPage from 'utils/hooks/useCurrentPage';

import Button from 'common/button/Button';
import Icon from 'common/Icon/Icon';

import styles from './InputGroupButtons.module.scss';
import inputStyles from './Inputs.module.scss';

/**
 * INDIVIDUAL INPUT BUTTON COMPONENT
 * Handles individual radio buttons and checkboxes within the button group
 * Contains the core logic for radio vs checkbox behavior and cascading callbacks
 */
const InputButton = props => {
  const { type, label, value, field, form, childValues, onValueChangedCallback, standardInput } =
    props;
  const { isInsightsCapture } = useCurrentPage();

  /**
   * Updates form value, converting empty arrays to empty strings for Formik validation
   * @param {*} fieldValue - The new value to set
   */
  const setValue = fieldValue => {
    if (fieldValue?.length === 0) {
      // set empty arrays as '' so that the formik validation works
      return form.setFieldValue(field.name, '');
    }
    return form.setFieldValue(field.name, fieldValue);
  };

  /**
   * CORE CLICK HANDLER - Manages radio/checkbox selection and cascading updates
   * Handles the critical difference between radio (single selection) and checkbox (multi-selection) behavior
   */
  const onClick = event => {
    event.preventDefault();
    event.stopPropagation();

    // CASCADING LOGIC: Handle parent-child relationships if this field has child values
    if (childValues) {
      if (type === 'radio') {
        // RADIO BUTTON CASCADING: Pass only single selected value
        // This is critical - radio buttons should only pass one value, not arrays
        const selectedParents = field.checked ? [] : [value];
        onValueChangedCallback({
          selectedParents,
          parentId: field.name,
          existingValues: form.values,
          setFormValue: form.setFieldValue,
        });
      } else {
        // CHECKBOX CASCADING: Handle multi-value selection logic
        const filteredValues = Array.isArray(form.values[field.name])
          ? form.values[field.name]?.filter(val => val !== value)
          : [];

        const addedValues = Array.isArray(form.values[field.name])
          ? [value, ...form.values[field.name]]
          : [value];

        const selectedParents = field.checked ? filteredValues : addedValues;

        onValueChangedCallback({
          selectedParents,
          parentId: field.name,
          existingValues: form.values,
          setFormValue: form.setFieldValue,
        });
      }
    }

    // RADIO BUTTON LOGIC: Single selection with deselection capability
    if (type === 'radio') {
      // If clicking a different option, select it
      if (form.values[field.name] !== value) {
        return setValue(value);
      }

      // If clicking the same option, deselect it and trigger cascading clear
      if (childValues) {
        onValueChangedCallback({
          selectedParents: [],
          parentId: field.name,
          existingValues: form.values,
          setFormValue: form.setFieldValue,
        });
      }
      return setValue('');
    }

    // CHECKBOX LOGIC: Multi-selection toggle behavior
    if (form.values[field.name]?.includes(value)) {
      // Remove value if already selected
      return setValue(form.values[field.name]?.filter(item => item !== value));
    }

    if (!form.values[field.name]) {
      // First selection - create array with single value
      return setValue([value]);
    }

    // Add value to existing selections
    return setValue([...form.values[field.name], value]);
  };

  // STANDARD INPUT RENDERING: Desktop vs Mobile styling for custom inputs
  if (standardInput) {
    const desktopStyle = field.checked ? 'input-item-desktop-selected' : 'input-item-desktop';
    return (
      <div
        className={!isMobileOnly ? styles[desktopStyle] : styles['input-item']}
        onClick={onClick}
      >
        {!isMobileOnly && <div className={styles['desktop-label']}>{label}</div>}
        {!isMobileOnly && (
          <Icon name={field.checked ? 'tickSelector' : 'emptySelector'} size={32} />
        )}
        {/* MOBILE RADIO BUTTONS */}
        {type === 'radio' && isMobileOnly && (
          <Icon name={field.checked ? 'radioButtonSelected' : 'radioButton'} size={18} />
        )}

        {/* MOBILE CHECKBOXES */}
        {type === 'checkbox' && isMobileOnly && (
          <Icon
            name={field.checked ? 'tickedCheckBox' : 'emptyCheckBox'}
            className={field.checked ? styles['color-check'] : ''}
            size={18}
          />
        )}

        {isMobileOnly && <div>{label}</div>}
      </div>
    );
  }

  // INSIGHTS CAPTURE STYLING: Special button styling for insights pages
  if (isInsightsCapture && isDesktop) {
    return (
      <Button
        size="small"
        buttonType={field.checked ? 'insights-checked' : 'filter'}
        onClick={onClick}
      >
        {label}
      </Button>
    );
  }

  // DEFAULT BUTTON STYLING: Standard filter button appearance
  return (
    <Button size="small" buttonType={field.checked ? 'filter-primary' : 'filter'} onClick={onClick}>
      {label}
    </Button>
  );
};

InputButton.propTypes = {
  label: PropTypes.string,
  type: PropTypes.oneOf(['radio', 'checkbox']),
  value: PropTypes.any,
  field: PropTypes.object,
  form: PropTypes.object,
  childValues: PropTypes.array,
  onValueChangedCallback: PropTypes.func,
  standardInput: PropTypes.bool,
};

InputButton.defaultProps = {
  label: '',
  type: 'checkbox',
  value: undefined,
  field: {},
  form: {},
  childValues: undefined,
  onValueChangedCallback: undefined,
  standardInput: false,
};

/**
 * INPUT GROUP BUTTONS COMPONENT
 * Renders a group of related radio buttons or checkboxes
 * Supports cascading relationships and automatic disabling when no options are available
 *
 * Key Features:
 * - Radio button groups (single selection)
 * - Checkbox groups (multi-selection)
 * - Cascading field dependencies (parent-child relationships)
 * - Automatic disabling when parent field has no selection
 * - Responsive design (desktop vs mobile styling)
 * - Integration with Formik for form state management
 */
export const InputGroupButtons = ({
  className,
  type,
  groupItems,
  required,
  error,
  label,
  name,
  onValueChangedCallback,
  standardInput,
}) => {
  // CASCADING DISABLED STATE: Disable field when no options are available
  // This typically happens when a parent field hasn't been selected yet
  const isDisabled = groupItems.length === 0;

  return (
    <div
      role="group"
      aria-labelledby={`${type}-group`}
      className={`
    ${inputStyles['field-container']} 
    ${required ? inputStyles.required : ''} 
    ${isDisabled ? inputStyles.disabled : ''}
    ${className}`}
    >
      <label htmlFor={name}>
        <span className={inputStyles.label}>{label}</span>
        {/* DISABLED STATE: Show helper message when field is disabled */}
        {isDisabled ? (
          <div className={styles['disabled-message']}>Select parent option first</div>
        ) : (
          /* ENABLED STATE: Render button group */
          <div className={standardInput ? '' : styles['input-group']}>
            {groupItems.map((item, index) => (
              // eslint-disable-next-line jsx-a11y/label-has-associated-control
              <label
                htmlFor={name}
                className={standardInput ? '' : styles['item-container']}
                key={`${name}-${index}`}
              >
                {/* FORMIK FIELD INTEGRATION: Each button is a Formik Field component */}
                <Field
                  key={`${item.label}-${index}`}
                  className={error ? styles.error : ''}
                  id={name}
                  name={name}
                  label={item.label}
                  type={type}
                  value={item.value}
                  standardInput={standardInput}
                  childValues={item.childValues} // For cascading relationships
                  onValueChangedCallback={onValueChangedCallback} // For cascading updates
                  component={InputButton}
                />
              </label>
            ))}
          </div>
        )}
        {/* ERROR HANDLING: Display validation errors */}
        <ErrorMessage name={name}>
          {message => <span className={inputStyles.error}>{message}</span>}
        </ErrorMessage>
        {error && <span className={inputStyles.error}>{error.replace(name, label)}</span>}
      </label>
    </div>
  );
};

InputGroupButtons.propTypes = {
  label: PropTypes.string,
  className: PropTypes.string,
  type: PropTypes.oneOf(['radio', 'checkbox']),
  groupItems: PropTypes.array,
  required: PropTypes.bool,
  error: PropTypes.string,
  name: PropTypes.string,
  onValueChangedCallback: PropTypes.func,
  standardInput: PropTypes.bool,
};

InputGroupButtons.defaultProps = {
  label: undefined,
  className: '',
  type: 'checkbox',
  groupItems: [],
  required: false,
  error: undefined,
  name: undefined,
  onValueChangedCallback: undefined,
  standardInput: false,
};
